import CloseRounded from '@mui/icons-material/CloseRounded';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { useAuth } from '../auth/AuthProvider';
import { sitemap } from '../routes/sitemap';
import { Logo } from './Logo';

export interface SidebarProps {
  readonly buildSha: string;
  readonly collapsed?: boolean;
  readonly environmentLabel: string;
  readonly mobile?: boolean;
  readonly onClose?: () => void;
  readonly version: string;
}

export function Sidebar({
  buildSha,
  collapsed = false,
  environmentLabel,
  mobile = false,
  onClose,
  version,
}: SidebarProps) {
  const auth = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const can = (permission: string, capability: string) =>
    auth.status === 'authenticated' &&
    auth.principal.permissions.includes(permission) &&
    (capability !== 'reportExports' || auth.principal.permissions.includes('REPORT_VIEW'));

  return (
    <Box
      component="nav"
      aria-label={t('shell.primaryNavigation')}
      sx={{
        bgcolor: 'navigation.background',
        color: 'navigation.text',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowX: 'hidden',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'space-between',
          minHeight: collapsed ? 72 : 88,
          px: collapsed ? 1.5 : 2,
          py: collapsed ? 1.5 : 2,
        }}
      >
        <Logo buildSha={buildSha} collapsed={collapsed} />
        {mobile ? (
          <IconButton aria-label={t('shell.closeNavigation')} onClick={onClose} size="small">
            <CloseRounded />
          </IconButton>
        ) : null}
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {sitemap.map((group) => {
          const visibleItems = group.items.filter((item) => can(item.permission, item.capability));
          if (visibleItems.length === 0) return null;

          return (
            <Box component="section" key={group.id} sx={{ mb: 1.5 }}>
              {!collapsed ? (
                <Typography
                  color="text.secondary"
                  component="h2"
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    px: 2,
                    py: 1,
                  }}
                >
                  {t(group.labelKey).toUpperCase()}
                </Typography>
              ) : null}
              <List aria-label={collapsed ? t(group.labelKey) : undefined} disablePadding>
                {visibleItems.map((item) => {
                  const selected = location.pathname === item.path;
                  const label = t(item.labelKey);
                  const Icon = item.icon;
                  const itemButton = (
                    <ListItemButton
                      aria-label={collapsed ? label : undefined}
                      component={Link}
                      onClick={mobile ? onClose : undefined}
                      selected={selected}
                      sx={{
                        borderRadius: 2,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        mx: 1,
                        px: collapsed ? 1 : 1.5,
                      }}
                      to={item.path}
                    >
                      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 34 }}>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      {collapsed ? null : (
                        <ListItemText primary={label} slotProps={{ primary: { noWrap: true } }} />
                      )}
                    </ListItemButton>
                  );

                  return collapsed ? (
                    <Tooltip key={item.path} placement="right" title={label}>
                      {itemButton}
                    </Tooltip>
                  ) : (
                    <Box key={item.path}>{itemButton}</Box>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {!collapsed ? (
        <EnvironmentCard
          buildSha={buildSha}
          environmentLabel={environmentLabel}
          version={version}
        />
      ) : null}
    </Box>
  );
}

function EnvironmentCard({
  buildSha,
  environmentLabel,
  version,
}: Pick<SidebarProps, 'buildSha' | 'environmentLabel' | 'version'>) {
  const shortCommit = buildSha === 'local' ? version : buildSha.slice(0, 7);

  return (
    <Box
      aria-label="Environment"
      role="group"
      sx={{
        background:
          'linear-gradient(135deg, rgba(31, 111, 235, 0.2) 0%, rgba(92, 63, 211, 0.24) 58%, rgba(111, 45, 189, 0.28) 100%)',
        border: '1px solid rgba(101, 160, 255, 0.24)',
        borderRadius: 2.5,
        boxShadow: '0 10px 28px rgba(6, 18, 40, 0.2)',
        flexShrink: 0,
        m: 1.5,
        px: 1.75,
        py: 1.35,
      }}
      title={buildSha}
    >
      <Typography
        color="text.primary"
        sx={{ fontSize: '0.67rem', fontWeight: 800, letterSpacing: '0.08em', lineHeight: 1.4 }}
      >
        ENVIRONMENT
      </Typography>
      <Typography noWrap sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
        {environmentLabel} · {shortCommit}
      </Typography>
    </Box>
  );
}
